import type React from "react";
import { ErrorSeverity } from "../../../error-handling/error-handler";
import { errorUI } from "../../../error-handling/index";
import { MarkdownExporter } from "../../../summary/markdown-exporter";
import { formatTranscript } from "../../../summary/transcript-formatter";
import type { Session, SessionSummary } from "../../../types/core";
import { renderMarkdown } from "../../../utils/markdown";
import { UI_ICONS } from "../../icons";
import { Modal } from "./Modal";

interface SummaryModalProps {
	isOpen: boolean;
	onClose: () => void;
	summary: SessionSummary | null;
	session: Session | null;
}

export const SummaryModal: React.FC<SummaryModalProps> = ({
	isOpen,
	onClose,
	summary,
	session,
}) => {
	const exporter = new MarkdownExporter();

	if (!isOpen || !summary) return null;

	const handleExportSummary = () => {
		exporter.downloadAsFile(summary, undefined, { includeTranscript: false });
	};

	const handleExportFull = () => {
		// Use latest transcript from session if available, otherwise fallback to the one captured in the summary
		const latestTranscript = session ? formatTranscript(session) : undefined;
		exporter.downloadAsFile(summary, latestTranscript, {
			includeTranscript: true,
		});
	};

	const handleExportJson = () => {
		if (session) {
			exporter.downloadAsJson(session, summary);
		}
	};

	const handleCopySummary = async () => {
		try {
			await navigator.clipboard.writeText(summary.overallSummary);
			errorUI.showNotification(
				"要約をクリップボードにコピーしました。",
				ErrorSeverity.LOW,
			);
		} catch (err) {
			console.error("Failed to copy summary:", err);
			errorUI.showNotification("コピーに失敗しました。", ErrorSeverity.MEDIUM);
		}
	};

	return (
		<Modal title="セッション要約" isOpen={isOpen} onClose={onClose}>
			<div className="flex flex-col gap-6 max-h-[70dvh]">
				{summary.title && (
					<h3 className="m-0 text-xl font-bold text-text-main border-b border-border-main pb-3">
						{summary.title}
					</h3>
				)}

				<div className="bg-bg-main border border-border-main rounded-xl p-5 flex flex-col gap-3 min-h-0 overflow-hidden">
					<div
						className="text-sm leading-relaxed overflow-y-auto prose prose-invert max-w-none"
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized by renderMarkdown
						dangerouslySetInnerHTML={{
							__html: renderMarkdown(summary.overallSummary, 2),
						}}
					/>
				</div>

				<div className="text-xs text-text-dim font-mono opacity-50">
					ID: {summary.sessionId}
				</div>

				<div className="flex justify-between items-center gap-4 mt-2 flex-wrap">
					<div className="flex gap-2 flex-wrap">
						<button
							type="button"
							className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
							onClick={handleCopySummary}
							title="クリップボードにコピー"
						>
							{UI_ICONS.COPY} コピー
						</button>
						<button
							type="button"
							className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
							onClick={handleExportSummary}
						>
							{UI_ICONS.SUMMARY} 要約を保存
						</button>
						<button
							type="button"
							className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm active:scale-95"
							onClick={handleExportFull}
						>
							{UI_ICONS.TRANSCRIPT} 全文を保存
						</button>
						<button
							type="button"
							className="bg-bg-main border border-border-main text-text-dim px-4 py-2 rounded-md cursor-pointer transition-all hover:border-text-dim hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none inline-flex items-center gap-2 text-sm disabled:opacity-50 active:scale-95"
							onClick={handleExportJson}
							disabled={!session}
							title={!session ? "セッション情報がありません" : ""}
						>
							{UI_ICONS.SETTINGS} JSONを保存
						</button>
					</div>
					<button
						type="button"
						className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white border-none rounded-lg cursor-pointer font-semibold transition-all hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none shadow-lg shadow-primary/20 active:scale-95"
						onClick={onClose}
					>
						閉じる
					</button>
				</div>
			</div>
		</Modal>
	);
};
